import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export interface CountryPhone {
  code: string;
  name: string;
  flag: string;
  iso: string;
  // Ejemplo del movil tal como queda guardado: sin codigo de pais ni cero de
  // larga distancia. Es solo visual (lleva espacios). El usuario NO tiene que
  // escribirlo asi: `normalizePhoneInput` acepta como lo tenga en la agenda.
  placeholder: string;
  // Cantidad exacta de digitos que quedan despues de normalizar.
  digits: number;
  // Validacion propia del pais, si la generica (largo exacto, sin 0) no
  // alcanza para detectar un numero mal escrito.
  nationalPattern?: RegExp;
  // Lo que WhatsApp exige entre el codigo de pais y el numero, y que el
  // usuario no escribe. Argentina: los moviles van `+54 9 11 2345 6789`; sin
  // ese `9` el mensaje no se entrega. Lo agrega `toE164`, nunca el usuario.
  e164Prefix?: string;
  // Prefijo de movil del marcado local, que no viaja en el numero
  // internacional. Argentina: `011 15 2345-6789` es `11 2345 6789`.
  localMobilePrefix?: string;
}

export const COUNTRY_PHONE_OPTIONS: CountryPhone[] = [
  { code: '+56', name: 'Chile', flag: 'chile.png', iso: 'CHL', placeholder: '9 1234 5678', digits: 9 },
  { code: '+58', name: 'Venezuela', flag: 'venezuela.png', iso: 'VEN', placeholder: '412 1234567', digits: 10 },
  {
    code: '+54', name: 'Argentina', flag: 'argentina.png', iso: 'ARG', placeholder: '11 2345 6789', digits: 10,
    // Todas las areas empiezan con 11, 2 o 3. Un numero que empieza con 15
    // (le falta el area) o con 9 (le sobro el 9 de WhatsApp) tiene 10 digitos
    // y pasaria la validacion generica apuntando a otro telefono.
    nationalPattern: /^(11\d{8}|[23]\d{9})$/,
    e164Prefix: '9',
    localMobilePrefix: '15',
  },
  { code: '+591', name: 'Bolivia', flag: 'bolivia.png', iso: 'BOL', placeholder: '7123 4567', digits: 8 },
  { code: '+55', name: 'Brasil', flag: 'brasil.png', iso: 'BRA', placeholder: '11 91234 5678', digits: 11 },
  { code: '+57', name: 'Colombia', flag: 'colombia.png', iso: 'COL', placeholder: '300 123 4567', digits: 10 },
  { code: '+593', name: 'Ecuador', flag: 'ecuador.png', iso: 'ECU', placeholder: '99 123 4567', digits: 9 },
  { code: '+595', name: 'Paraguay', flag: 'paraguay.png', iso: 'PRY', placeholder: '981 123 456', digits: 9 },
  { code: '+51', name: 'Perú', flag: 'peru.png', iso: 'PER', placeholder: '987 654 321', digits: 9 },
  { code: '+598', name: 'Uruguay', flag: 'uruguay.png', iso: 'URY', placeholder: '94 123 456', digits: 8 },
  { code: '+1', name: 'Estados Unidos', flag: 'usa.png', iso: 'USA', placeholder: '555 123 4567', digits: 10 },
];

// Valor por defecto: Chile
export const DEFAULT_COUNTRY = COUNTRY_PHONE_OPTIONS[0];

// Busca el pais por prefijo. Si no lo encuentra devuelve Chile, que es el
// mismo default que usa el selector.
export function getCountryByCode(code: string): CountryPhone {
  return COUNTRY_PHONE_OPTIONS.find((c) => c.code === code) || DEFAULT_COUNTRY;
}

// Forma del numero YA normalizado: largo exacto y sin cero adelante, o la
// regla propia del pais.
export function phonePatternFor(country: CountryPhone): RegExp {
  return country.nationalPattern ?? new RegExp(`^[1-9][0-9]{${country.digits - 1}}$`);
}

/**
 * Numero completo para WhatsApp: codigo de pais + prefijo movil + numero.
 * Es el unico lugar donde se arma; el OTP se envia, reenvia y verifica con
 * este valor, asi que tiene que ser siempre el mismo.
 */
export function toE164(code: string, phone: string): string {
  const prefix = getCountryByCode(code).e164Prefix ?? '';
  return `${code}${prefix}${phone}`;
}

/**
 * Si el usuario escribio el numero en formato internacional (`+54 ...` o
 * `0054 ...`), el pais que dice el numero. Sirve para corregir la bandera
 * cuando la que se eligio por IP no es la del telefono: un argentino que se
 * registra desde Chile pega su `+54 9 ...` y no tiene que tocar el selector.
 *
 * Solo mira lo que empieza con `+` o `00`: un numero nacional no dice de que
 * pais es. Ningun codigo de la tabla es prefijo de otro, asi que el primero
 * que calza es el unico.
 */
export function detectCountryFromInput(raw: string): CountryPhone | null {
  const compact = raw.replace(/[^\d+]/g, '');
  const intl = compact.startsWith('+') ? compact.slice(1)
    : compact.startsWith('00') ? compact.slice(2)
    : null;
  if (intl === null) return null;
  return COUNTRY_PHONE_OPTIONS.find((c) => intl.startsWith(c.code.slice(1))) ?? null;
}

/**
 * Lleva el numero como lo escriba o pegue el usuario a los digitos que se
 * guardan: sin simbolos, sin codigo de pais, sin el 9 de WhatsApp, sin el
 * cero de marcado nacional y sin el prefijo movil local.
 *
 * La regla es que nadie tenga que adivinar el formato. Todos estos son el
 * mismo numero argentino y dan `1123456789`:
 *   +54 9 11 2345-6789   (copiado de WhatsApp)
 *   +54 11 2345 6789     (sin el 9)
 *   9 11 2345 6789       (con el 9, sin el +54)
 *   011 15 2345-6789     (como se marca desde un fijo)
 *   11 15 2345 6789      (sin el 0)
 *   0054 9 11 2345 6789  (salida internacional)
 *
 * No recorta: si sobran o faltan digitos los devuelve igual, para que el
 * validador lo marque en vez de mandar el codigo a un numero equivocado.
 * El codigo de pais solo se saca si lo que queda tiene un largo posible, asi
 * un numero nacional que casualmente empieza igual no se toca.
 */
export function normalizePhoneInput(raw: string, country: CountryPhone): string {
  const cc = country.code.replace('+', '');
  const prefix = country.e164Prefix ?? '';
  const local = country.localMobilePrefix ?? '';
  const fits = (n: string) =>
    n.length === country.digits || (!!local && n.length === country.digits + local.length);

  let digits = raw.replace(/\D/g, '').replace(/^0+/, '');

  const heads = [cc + prefix, cc, prefix].filter((h) => h);
  const head = heads.find((h) => digits.startsWith(h) && fits(digits.slice(h.length).replace(/^0+/, '')));
  if (head) {
    // `+54 011 ...`: el cero nacional tambien aparece despues del codigo.
    digits = digits.slice(head.length).replace(/^0+/, '');
  }

  if (local && digits.length === country.digits + local.length) {
    digits = stripLocalMobilePrefix(digits, local);
  }

  return digits;
}

/**
 * Argentina marca `area + 15 + numero`. El area tiene 2, 3 o 4 digitos, asi
 * que el `15` se busca en esas posiciones. El unico area de 2 es `11`
 * (AMBA); para el resto gana la primera posicion donde aparece.
 */
function stripLocalMobilePrefix(digits: string, local: string): string {
  const positions = digits.startsWith('11') ? [2] : [3, 4];
  const at = positions.find((i) => digits.slice(i, i + local.length) === local);
  return at === undefined ? digits : digits.slice(0, at) + digits.slice(at + local.length);
}

/**
 * Validador del campo telefono. Juzga el numero ya normalizado, asi que el
 * campo puede tener `+54 9 11 2345-6789` mientras el usuario escribe. El pais
 * se pide en cada validacion porque la bandera puede cambiar sin recrear el
 * control.
 */
export function phoneValidatorFor(getCountry: () => CountryPhone): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw: string = control.value || '';
    if (!raw) return null; // eso lo dice `required`
    const country = getCountry();
    return phonePatternFor(country).test(normalizePhoneInput(raw, country))
      ? null
      : { pattern: true };
  };
}

// Ayuda que se muestra junto al campo.
export function phoneHintFor(country: CountryPhone): string {
  return `Escríbelo o pégalo como lo tengas, con o sin ${country.code}. Ej: ${country.placeholder}`;
}

// Mensaje de error del campo. Dice que esta mal en los casos que se pueden
// reconocer, en vez de solo repetir el ejemplo.
export function phoneErrorFor(country: CountryPhone, raw = ''): string {
  const digits = normalizePhoneInput(raw, country);
  const local = country.localMobilePrefix;
  if (local && digits.startsWith(local) && digits.length <= country.digits) {
    return `Falta el código de área antes del ${local}. Ej: ${country.placeholder}`;
  }
  if (digits.length && digits.length < country.digits) {
    return `Faltan dígitos: un número de ${country.name} tiene ${country.digits}. Ej: ${country.placeholder}`;
  }
  if (digits.length > country.digits) {
    return `Sobran dígitos para un número de ${country.name}. ¿Es de otro país? Ej: ${country.placeholder}`;
  }
  return `Revisa tu número de ${country.name}. Ej: ${country.placeholder}`;
}

// ==================== Contraseña ====================

// Reglas de contraseña. Es la UNICA fuente de verdad: de aca salen tanto el
// validador del formulario (utils/password-rules.ts) como el checklist que ve
// el usuario mientras escribe (password-rules-checklist).
// No agregar reglas en otro lado: si no esta en esta lista, no se valida.
export const PASSWORD_MIN_LENGTH = 8;

export const PASSWORD_SPECIAL_CHARS = /[!@#$%^&*()_+=\-{}\[\]:;"'<>,.?/\\|~`]/;

export interface PasswordRule {
  id: string;
  label: string;
  test: (value: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'length',
    label: `Mínimo ${PASSWORD_MIN_LENGTH} caracteres`,
    test: (value) => value.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: 'uppercase',
    label: 'Una letra mayúscula',
    test: (value) => /[A-Z]/.test(value),
  },
  {
    id: 'lowercase',
    label: 'Una letra minúscula',
    test: (value) => /[a-z]/.test(value),
  },
  {
    id: 'number',
    label: 'Un número',
    test: (value) => /\d/.test(value),
  },
  {
    id: 'special',
    label: 'Un carácter especial (!@#$…)',
    test: (value) => PASSWORD_SPECIAL_CHARS.test(value),
  },
];

// ==================== Numeros de paso ====================

// Los numeros de paso viven aca y en ningun otro lado. Antes estaban repartidos
// en 39 literales entre `.ts` y `.html`, y esa dispersion tenia dos costos
// concretos: reordenar el flujo era imposible sin cazar cada `goToStep(9)` a
// mano, y ya se habia colado una regla del flujo equivocado (`prevStep` trataba
// el paso 9 como la declaracion PEP de persona, que en empresa es el e-RUT).
//
// Un componente compartido usa `PASOS_COMUNES` cuando el destino es el mismo en
// los dos flujos, y el mapa del tipo de cuenta cuando no.

/** Pasos 1 a 4: identicos para persona y empresa. */
export const PASOS_COMUNES = {
  TIPO_CUENTA: 1,
  FORMULARIO: 2,
  OTP_EMAIL: 3,
  OTP_WHATSAPP: 4,
} as const;

export const PASOS_PERSONA = {
  ...PASOS_COMUNES,
  OCR: 5,
  SELFIE: 6,
  CONFIRMACION: 7,
  PEP_SELECCION: 8,
  PEP_DECLARACION: 9,
} as const;

/**
 * El e-RUT va ANTES de la verificacion de identidad, y no despues.
 *
 * Es el unico punto del flujo que corta una empresa ya registrada, y ese corte
 * necesita el RUT de la sociedad, que recien se conoce al analizar el PDF. Con
 * el e-RUT al final, a la empresa duplicada se le pedia documento, reverso y
 * selfie para recien despues decirle que no podia seguir: el trabajo mas caro
 * del registro, tirado. Ahora el PDF —que no necesita camara ni permisos— filtra
 * primero, y la pantalla de consentimiento queda pegada a la captura que
 * consiente.
 */
export const PASOS_EMPRESA = {
  ...PASOS_COMUNES,
  ERUT: 5,
  CONSENTIMIENTO: 6,
  OCR: 7,
  SELFIE: 8,
  REPRESENTANTE: 9,
  PEP: 10,
  PERSONA_JURIDICA: 11,
  NEGOCIO: 12,
} as const;

/** Primer paso del tramo propio de empresa: antes son los 4 compartidos. */
export const PRIMER_PASO_EMPRESA = PASOS_EMPRESA.ERUT;

// Total de pasos por tipo de cuenta. Persona suma uno mas si declara vinculos
// PEP: la declaracion es el paso 9 y solo existe en ese caso.
export const STEPS_PERSONAL = PASOS_PERSONA.PEP_SELECCION;
export const STEPS_EMPRESA = PASOS_EMPRESA.NEGOCIO;

// Empresa con el flujo nuevo apagado (`empresaSignupV2Enabled: false`): el
// registro termina en el OTP de WhatsApp y sale al dashboard, como antes de
// este refactor. Es el valor al que vuelve `totalSteps` con el kill-switch.
export const STEPS_EMPRESA_LEGACY = PASOS_COMUNES.OTP_WHATSAPP;

// Motivo de la relacion comercial de la empresa (paso de persona juridica).
// Los `value` son los que ya guarda el registro de empresa del dashboard.
export interface MotivoRelacionOption {
  value: string;
  label: string;
}

export const MOTIVOS_RELACION_COMERCIAL: MotivoRelacionOption[] = [
  { value: 'ahorro', label: 'Ahorro' },
  { value: 'inversion', label: 'Inversión' },
  { value: 'trading', label: 'Trading' },
  { value: 'pago_servicios', label: 'Pago de servicios' },
  { value: 'otros', label: 'Otros' },
];

// Opciones de género
export interface GenderOption {
  value: string;
  label: string;
}

export const GENDER_OPTIONS: GenderOption[] = [
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Femenino' },
];

// Opciones de volumen transaccional mensual
export interface VolumeOption {
  value: string;
  label: string;
}

export const VOLUME_OPTIONS: VolumeOption[] = [
  { value: 'menor a 1000', label: 'Menor a 1.000 USD' },
  { value: 'menor a 2000', label: 'Menor a 2.000 USD' },
  { value: 'menor a 3000', label: 'Menor a 3.000 USD' },
  { value: 'mayor a 3000', label: 'Mayor a 3.000 USD' },
];

// Lista de profesiones
export const PROFESSIONS: string[] = [
  'Administrador/a de Empresas',
  'Analista Financiero/a',
  'Contador/a',
  'Abogado/a',
  'Ingeniero/a Civil',
  'Ingeniero/a Eléctrico/a',
  'Ingeniero/a Químico/a',
  'Ingeniero/a Mecánico/a',
  'Ingeniero/a Comercial',
  'Ingeniero/a de Software',
  'Desarrollador/a Web',
  'Desarrollador/a Móvil',
  'Diseñador/a UX/UI',
  'Diseñador/a Gráfico/a',
  'Gerente de Proyecto',
  'Especialista en Recursos Humanos',
  'Coordinador/a de Onboarding',
  'Ejecutivo/a de Ventas',
  'Coordinador/a de Marketing',
  'Community Manager',
  'Analista de Datos',
  'Científico/a de Datos',
  'Consultor/a',
  'Arquitecto/a de Soluciones',
  'Administrador/a de Sistemas / DevOps',
  'Técnico/a de Soporte',
  'Ingeniero/a de Quality Assurance',
  'Especialista en Seguridad Informática',
  'Consultor/a de Compliance',
  'Auditor/a Interno/a',
  'Psicólogo/a Organizacional',
  'Gerente de Producto',
  'Analista de Negocios',
  'Especialista en Customer Success',
  'Copywriter / Redactor/a de Contenidos',
  'Médico/a',
  'Enfermero/a',
  'Odontólogo/a',
  'Profesionales de la Salud',
  'Profesionales de la Seguridad (Carabineros de Chile, PDI, Guardia, etc.)',
  'Otro (especificar)',
];

// Mapeo de códigos de nacionalidad a nombres de país
export const NATIONALITY_MAP: Record<string, string> = {
  'CHL': 'Chile',
  'CHILE': 'Chile',
  'CHILENO': 'Chile',
  'CHILENA': 'Chile',
  'VEN': 'Venezuela',
  'VENEZUELA': 'Venezuela',
  'VENEZOLANO': 'Venezuela',
  'VENEZOLANA': 'Venezuela',
  'ARG': 'Argentina',
  'ARGENTINA': 'Argentina',
  'ARGENTINO': 'Argentina',
  'BOL': 'Bolivia',
  'BOLIVIA': 'Bolivia',
  'BOLIVIANO': 'Bolivia',
  'BOLIVIANA': 'Bolivia',
  'BRA': 'Brasil',
  'BRASIL': 'Brasil',
  'BRASILEÑO': 'Brasil',
  'BRASILEÑA': 'Brasil',
  'COL': 'Colombia',
  'COLOMBIA': 'Colombia',
  'COLOMBIANO': 'Colombia',
  'COLOMBIANA': 'Colombia',
  'ECU': 'Ecuador',
  'ECUADOR': 'Ecuador',
  'ECUATORIANO': 'Ecuador',
  'ECUATORIANA': 'Ecuador',
  'PRY': 'Paraguay',
  'PARAGUAY': 'Paraguay',
  'PARAGUAYO': 'Paraguay',
  'PARAGUAYA': 'Paraguay',
  'PER': 'Perú',
  'PERU': 'Perú',
  'PERUANO': 'Perú',
  'PERUANA': 'Perú',
  'URY': 'Uruguay',
  'URUGUAY': 'Uruguay',
  'URUGUAYO': 'Uruguay',
  'URUGUAYA': 'Uruguay',
  'USA': 'Estados Unidos',
  'ESTADOUNIDENSE': 'Estados Unidos',
};

// Mapeo de tipos de documento
export const DOCUMENT_TYPE_MAP: Record<string, string> = {
  'CEDULA_IDENTIDAD_EXTRANJERO': 'Cédula de Identidad (Extranjero)',
  'CEDULA_IDENTIDAD': 'Cédula de Identidad',
  'RUT': 'RUT',
  'PASAPORTE': 'Pasaporte',
};

// ==================== PEP (Persona Expuesta Politicamente) ====================

// Opciones de parentesco con PEP
export interface ParentescoOption {
  value: string;
  label: string;
}

export const PARENTESCO_OPTIONS: ParentescoOption[] = [
  { value: 'yo_mismo', label: 'Yo mismo' },
  { value: 'conyuge', label: 'Cónyuge' },
  { value: 'padre', label: 'Padre' },
  { value: 'madre', label: 'Madre' },
  { value: 'hijo', label: 'Hijo(a)' },
  { value: 'hermano', label: 'Hermano(a)' },
  { value: 'abuelo', label: 'Abuelo(a)' },
  { value: 'nieto', label: 'Nieto(a)' },
  { value: 'socio', label: 'Socio comercial / Pacto de actuación conjunta' },
  { value: 'otro', label: 'Otro' },
];

// Cargos PEP según normativa chilena
export const PEP_CARGOS: string[] = [
  'Presidente de la República',
  'Senador / Diputado',
  'Alcalde',
  'Ministro de Estado',
  'Subsecretario',
  'Intendente / Gobernador',
  'Secretario Regional Ministerial',
  'Embajador',
  'Jefe Superior de Servicio',
  'Ministro Corte Suprema',
  'Ministro Corte de Apelaciones',
  'Comandante en Jefe de las Fuerzas Armadas',
  'Director General de Carabineros',
  'Director General de Investigaciones (PDI)',
  'Fiscal Nacional del Ministerio Público',
  'Fiscal Regional',
  'Contralor General de la República',
  'Consejero del Banco Central de Chile',
  'Consejero del Consejo de Defensa del Estado',
  'Ministro del Tribunal Constitucional',
  'Ministro del Tribunal de la Libre Competencia',
  'Integrante del Tribunal de Contratación Pública',
  'Consejero del Consejo de Alta Dirección Pública',
  'Director o ejecutivo principal de empresa pública',
  'Director de sociedad anónima nombrado por el Estado',
  'Miembro de directiva de partido político',
  'Otro cargo de alta dirección pública',
];

// Lista detallada de cargos PEP para la declaración
export const PEP_DECLARATION_LIST: string[] = [
  'Presidente de la República',
  'Senadores, Diputados y Alcaldes',
  'Ministros de la Corte Suprema y Cortes de Apelaciones',
  'Ministros de Estado, Subsecretarios, Intendentes, Gobernadores, Secretarios Regionales Ministeriales, Embajadores, Jefes Superiores de Servicio (centralizados y descentralizados)',
  'Comandantes en Jefe de las Fuerzas Armadas, Director General de Carabineros, Director General de Investigaciones',
  'Fiscal Nacional del Ministerio Público y Fiscales Regionales',
  'Contralor General de la República',
  'Consejeros del Banco Central de Chile',
  'Consejeros del Consejo de Defensa del Estado',
  'Ministros del Tribunal Constitucional',
  'Ministros del Tribunal de la Libre Competencia',
  'Integrantes titulares y suplentes del Tribunal de Contratación Pública',
  'Consejeros del Consejo de Alta Dirección Pública',
  'Directores y ejecutivos principales de empresas públicas según Ley N° 18.045',
  'Directores de sociedades anónimas nombrados por el Estado o sus organismos',
  'Miembros de las directivas de los partidos políticos',
];
